/**
 * The calculation workspace: inputs | results | report (CLAUDE.md 14.1, 14.3).
 *
 * This page is the template every module page will copy, so the behaviours that
 * matter are established here rather than per module:
 *
 * - **Live preview**, debounced 500 ms, in-flight request cancelled on new input.
 * - **Draft autosave to localStorage**, keyed by position id. Engineers lose work; a
 *   dropped connection must not cost an hour (14.1). This was specified and missing.
 * - **Staleness is loud.** Both kinds: results that no longer match the form, and a
 *   saved position whose fingerprint no longer matches its inputs (5.7).
 * - **The pinned quadruple is always visible** -- module, version, code, parameter
 *   set. Reproducibility the user cannot see buys no confidence.
 * - **429 is retried, not shown as a failure.** `/calc/preview` sheds load rather than
 *   queueing, so a busy server is a "try again in a moment", not an error the
 *   engineer caused.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ApiError, api } from '@/api/client'
import { useCalculatePosition, usePosition, useProject, useUpdatePosition } from '@/api/hooks'
import type { PreviewResponse, Status, TraceNode } from '@/api/types'
import { AppShell } from '@/components/layout/AppShell'
import { PositionIdentity } from '@/components/calc/PositionIdentity'
import { PositionInputs } from '@/components/calc/PositionInputs'
import { ReportPanel } from '@/components/calc/ReportPanel'
import { ResultsPanel } from '@/components/calc/ResultsPanel'
import { ReviewControls } from '@/components/calc/ReviewControls'
import { RevisionsPanel } from '@/components/calc/RevisionsPanel'
import { TraceView, VERBOSITY, type Verbosity } from '@/components/calc/TraceView'
import { BeamDiagram } from '@/components/diagrams/BeamDiagram'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { UserMenu } from '@/components/layout/UserMenu'
import { useToast } from '@/components/ui/toast'
import { formatUtilisation } from '@/lib/format'
import { ProjectTree } from '@/components/layout/ProjectTree'

const PREVIEW_DEBOUNCE_MS = 500
const DRAFT_PREFIX = 'calcdesk.draft.'

/** Read a saved draft. Storage can throw outright in a locked-down browser. */
function readDraft(positionId: number): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(`${DRAFT_PREFIX}${positionId}`)
    return raw ? (JSON.parse(raw) as Record<string, number>) : null
  } catch {
    return null
  }
}

export function PositionPage() {
  const { t } = useTranslation(['common', 'report'])
  const { toast } = useToast()
  const params = useParams()
  const positionId = Number(params.positionId)

  const { data: position } = usePosition(positionId)
  const { data: project } = useProject(position?.project_id ?? null)
  const updatePosition = useUpdatePosition(positionId)
  const calculate = useCalculatePosition(positionId)

  const [inputs, setInputs] = useState<Record<string, number> | null>(null)
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [verbosity, setVerbosity] = useState<Verbosity>(VERBOSITY.NORMAL)

  const abortRef = useRef<AbortController | null>(null)
  const retryTimer = useRef<number | null>(null)

  // The form's values are *derived* until the engineer touches something: the saved
  // position, or a local draft if one survived their last visit. Deriving rather than
  // seeding through an effect avoids a render where the form is empty, and avoids the
  // cascading re-render that copying props into state always costs.
  const values = useMemo(() => {
    if (inputs !== null) return inputs
    if (!position) return null
    return readDraft(position.id) ?? (position.inputs as Record<string, number>)
  }, [inputs, position])

  // Tell the engineer their unsaved work came back. A toast is an external system, so
  // this is what an effect is actually for.
  const announcedDraft = useRef<number | null>(null)
  useEffect(() => {
    if (!position || announcedDraft.current === position.id) return
    announcedDraft.current = position.id
    if (readDraft(position.id)) {
      toast({ title: t('draft.restored'), variant: 'info' })
    }
  }, [position, toast, t])

  // Autosave the draft on every change (14.1).
  useEffect(() => {
    if (!position || !inputs) return
    try {
      localStorage.setItem(`${DRAFT_PREFIX}${position.id}`, JSON.stringify(inputs))
    } catch {
      // Private window: the draft simply will not survive a reload.
    }
  }, [inputs, position])

  const runPreviewRef = useRef<
    ((values: Record<string, number>, moduleId: string, codeId: string) => Promise<void>) | null
  >(null)

  const runPreview = useCallback(
    async (values: Record<string, number>, moduleId: string, codeId: string) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setPending(true)
      try {
        const response = await api.publicPost<PreviewResponse>(
          '/calc/preview',
          { module_id: moduleId, code_id: codeId, inputs: values, verbosity },
          controller.signal,
        )
        setPreview(response)
        setPreviewError(null)
      } catch (error) {
        if (controller.signal.aborted) return
        if (error instanceof ApiError && error.isRateLimited) {
          // The server shed load rather than queueing. Retrying quietly is right:
          // the engineer did nothing wrong and the previous numbers stay marked stale.
          const delay = (error.retryAfter ?? 2) * 1000
          // Via a ref: referring to `runPreview` inside its own initialiser is a
          // temporal-dead-zone bug waiting for the first 429 in production.
          retryTimer.current = window.setTimeout(
            () => void runPreviewRef.current?.(values, moduleId, codeId),
            delay,
          )
          return
        }
        setPreviewError(error instanceof ApiError ? error.localised : String(error))
      } finally {
        if (!controller.signal.aborted) setPending(false)
      }
    },
    [verbosity],
  )

  // Kept current in an effect rather than during render: React may render without
  // committing, and a ref written during render can then point at a function from
  // a discarded pass.
  useEffect(() => {
    runPreviewRef.current = runPreview
  }, [runPreview])

  // Debounced live preview (14.1).
  useEffect(() => {
    if (!values || !position) return
    const timer = window.setTimeout(
      () => void runPreview(values, position.module_id, position.code_id),
      PREVIEW_DEBOUNCE_MS,
    )
    return () => {
      window.clearTimeout(timer)
      if (retryTimer.current) window.clearTimeout(retryTimer.current)
    }
  }, [values, position, runPreview])

  /** True when the form differs from what was last saved. */
  const dirty = useMemo(() => {
    if (!position || !values) return false
    return JSON.stringify(values) !== JSON.stringify(position.inputs)
  }, [values, position])

  const onSave = async () => {
    if (!position || !values) return
    try {
      await updatePosition.mutateAsync({ rowVersion: position.row_version, inputs: values })
      await calculate.mutateAsync()
      try {
        localStorage.removeItem(`${DRAFT_PREFIX}${position.id}`)
      } catch {
        // Non-fatal.
      }
      toast({ title: t('common.saved'), variant: 'success' })
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        // Optimistic locking: say *who* changed it, which is what makes the conflict
        // actionable rather than merely annoying (10.2).
        toast({
          title: t('errors:error.position.version_conflict'),
          variant: 'error',
        })
        return
      }
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  const status = (preview?.status ?? position?.result_summary?.status ?? null) as Status | null
  const checks = preview?.checks ?? position?.result_summary?.checks ?? []
  const warnings = preview?.warnings ?? position?.result_summary?.warnings ?? []
  const diagrams = preview?.diagrams ?? position?.result_summary?.diagrams ?? null
  const governing = preview?.governing_ratio ?? position?.result_summary?.governing_ratio ?? null

  const statusBanner = status && (
    <div className="flex flex-wrap items-center gap-3">
      <StatusBadge status={status} />
      {governing !== null && (
        <span className="tabular text-sm text-text-muted">
          {t('common.utilisation')} {formatUtilisation(governing)}
        </span>
      )}
    </div>
  )

  return (
    <AppShell
      title={position ? `${position.short_code} — ${position.name}` : t('nav.inputs')}
      statusBanner={statusBanner}
      header={
        position && (
          <PositionIdentity
            position={position}
            projectName={project?.name}
            dirty={dirty}
            onSave={onSave}
            saving={updatePosition.isPending || calculate.isPending}
          />
        )
      }
      toolbar={
        <>
          <LanguageToggle />
          <ThemeToggle />
          <UserMenu />
        </>
      }
      tree={position && <ProjectTree projectId={position.project_id} activeId={position.id} />}
      inputs={
        position &&
        values && (
          <div className="space-y-5">
            <PositionInputs moduleId={position.module_id} values={values} onChange={setInputs} />
            {position.review_state !== 'none' || position.status !== 'draft' ? (
              <ReviewControls position={position} />
            ) : null}
          </div>
        )
      }
      results={
        <div className="space-y-6">
          <ResultsPanel
            checks={checks}
            warnings={warnings}
            governingRatio={governing}
            status={status}
            // Two different kinds of staleness, both worth saying out loud.
            stale={dirty && !pending}
            pending={pending}
            error={previewError}
            onRetry={() =>
              position && values && void runPreview(values, position.module_id, position.code_id)
            }
          />

          {diagrams && (
            <div className="space-y-4">
              <BeamDiagram data={diagrams} kind="moment" />
              <BeamDiagram data={diagrams} kind="shear" />
            </div>
          )}

          <TraceView
            trace={(preview?.trace as TraceNode | undefined) ?? null}
            verbosity={verbosity}
            onVerbosityChange={setVerbosity}
          />

          {/* Under the results rather than beside the inputs, and inside the pane the
              tab bar already reaches, so the history is available at all four
              viewports without a fifth pane to navigate (14.3). */}
          {position && <RevisionsPanel position={position} />}
        </div>
      }
      report={position && <ReportPanel position={position} dirty={dirty} />}
    />
  )
}
